import { useEffect, useState } from "react";
import { Copy, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { title: "", category_slug: "it-ites", subcategory: "", short_description: "", description: "",
  duration: "", duration_months: 0, eligibility: "", level: "Beginner", mode: "Offline",
  fees: 0, discount: 0, certification: "", image: "", featured: false, status: "draft" };

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    api.get("/admin/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditing(c.id); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/admin/courses", form);
        toast.success("Course created");
      } else {
        await api.patch(`/admin/courses/${editing}`, form);
        toast.success("Course updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const duplicate = async (c) => {
    try {
      await api.post(`/admin/courses/${c.id}/duplicate`);
      toast.success("Course duplicated as draft");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleStatus = async (c) => {
    try {
      await api.patch(`/admin/courses/${c.id}`, { ...c, status: c.status === "published" ? "draft" : "published" });
      toast.success(c.status === "published" ? "Unpublished" : "Published");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-courses-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl font-bold text-white mb-1">Course Administration</h1>
          <p className="text-xs text-slate-500">{courses.length} courses in the catalogue.</p>
        </div>
        <Button onClick={openNew} className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition-colors" data-testid="new-course-btn">
          <Plus className="w-4 h-4 mr-2" /> New Course
        </Button>
      </div>

      <div className="bg-card border border-white/10 rounded-2xl overflow-x-auto" data-testid="courses-table">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/10">
              <th className="px-5 py-3.5">Course</th>
              <th className="px-5 py-3.5">Category</th>
              <th className="px-5 py-3.5">Fee</th>
              <th className="px-5 py-3.5">Status</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {courses.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-navy-hover/40 transition-colors" data-testid={`course-row-${c.slug}`}>
                <td className="px-5 py-3.5">
                  <div className="text-white font-medium">{c.title}
                    {c.demo && <span className="text-[9px] text-slate-500 border border-white/15 rounded-full px-1.5 py-0.5 ml-2 uppercase">Demo</span>}
                    {c.featured && <span className="text-[9px] text-orange-400 border border-orange-500/40 rounded-full px-1.5 py-0.5 ml-2 uppercase">Featured</span>}
                  </div>
                  <div className="text-xs text-slate-500">{c.duration} • {c.level}</div>
                </td>
                <td className="px-5 py-3.5 text-slate-400 text-xs">{c.category_slug}</td>
                <td className="px-5 py-3.5 text-slate-300">₹{Number(c.fees).toLocaleString("en-IN")}{c.discount > 0 && <span className="text-xs text-orange-400 ml-1">-{c.discount}%</span>}</td>
                <td className="px-5 py-3.5">
                  <button onClick={() => toggleStatus(c)}
                    className={`text-xs font-semibold border rounded-full px-3 py-1 transition-colors ${c.status === "published" ? "text-emerald-400 border-emerald-500/40" : "text-slate-400 border-white/20"}`}
                    data-testid={`course-status-${c.slug}`}>
                    {c.status}
                  </button>
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(c)} className="p-2 text-slate-400 hover:text-orange-400 transition-colors" data-testid={`course-edit-${c.slug}`} aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => duplicate(c)} className="p-2 text-slate-400 hover:text-orange-400 transition-colors" data-testid={`course-duplicate-${c.slug}`} aria-label="Duplicate"><Copy className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-navy-surface border-white/10 text-slate-200 sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{editing === "new" ? "New Course" : "Edit Course"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4" data-testid="course-form">
            <div className="sm:col-span-2">
              <Label>Title *</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-title" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category_slug} onValueChange={(v) => setForm({ ...form, category_slug: v })}>
                <SelectTrigger className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
                  {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subcategory</Label>
              <Input value={form.subcategory || ""} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-subcategory" />
            </div>
            <div>
              <Label>Duration (label)</Label>
              <Input placeholder="3 Months" value={form.duration || ""} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-duration" />
            </div>
            <div>
              <Label>Duration (months)</Label>
              <Input type="number" step="0.5" value={form.duration_months || 0} onChange={(e) => setForm({ ...form, duration_months: Number(e.target.value) })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-months" />
            </div>
            <div>
              <Label>Level</Label>
              <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                <SelectTrigger className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-level"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
                  {["Beginner", "Intermediate", "Advanced", "Degree", "All Levels"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
                  {["Offline", "Online", "Hybrid", "Offline / Hybrid", "Online / Offline", "Work-Integrated"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fee (₹)</Label>
              <Input type="number" value={form.fees || 0} onChange={(e) => setForm({ ...form, fees: Number(e.target.value) })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-fees" />
            </div>
            <div>
              <Label>Discount (%)</Label>
              <Input type="number" value={form.discount || 0} onChange={(e) => setForm({ ...form, discount: Number(e.target.value) })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-discount" />
            </div>
            <div className="sm:col-span-2">
              <Label>Eligibility</Label>
              <Input value={form.eligibility || ""} onChange={(e) => setForm({ ...form, eligibility: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-eligibility" />
            </div>
            <div className="sm:col-span-2">
              <Label>Certification</Label>
              <Input value={form.certification || ""} onChange={(e) => setForm({ ...form, certification: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-certification" />
            </div>
            <div className="sm:col-span-2">
              <Label>Short Description</Label>
              <Textarea rows={2} value={form.short_description || ""} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-shortdesc" />
            </div>
            <div className="sm:col-span-2">
              <Label>Full Description</Label>
              <Textarea rows={4} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-description" />
            </div>
            <div className="sm:col-span-2">
              <Label>Image URL</Label>
              <Input value={form.image || ""} onChange={(e) => setForm({ ...form, image: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="course-form-image" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="course-form-featured" /> Featured
            </label>
            <div className="sm:col-span-2 flex gap-3 pt-2">
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold transition-colors" data-testid="course-form-save">Save Course</Button>
              <Button type="button" variant="outline" onClick={() => setEditing(null)} className="border-white/20 text-white hover:bg-white/5 transition-colors" data-testid="course-form-cancel"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
